import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import {
  fetchForgeRecipes,
  fetchForgeQueue,
  startForge,
  collectForgeItem,
  type ForgeRecipe,
  type ForgeQueueEntry,
} from '../../api/forgeApi';
import { useWalletStore } from '../../store/walletStore';
const forgeIcon = '/icons/forge.svg';
import styles from './ForgeSection.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface InventoryEntry {
  item_id: string;
  quantity: number;
}

interface ForgeSectionProps {
  token: string;
  hideButton?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Formata segundos em MM:SS ou HH:MM:SS */
function formatTime(seconds: number): string {
  if (seconds <= 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ForgeSection({ token, hideButton = false }: ForgeSectionProps) {
  const { t } = useTranslation();

  // Modal state
  const [isOpen, setIsOpen]         = useState(false);
  const [isHovered, setIsHovered]   = useState(false);
  const [isAnimOpen, setIsAnimOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Data
  const [recipes, setRecipes]       = useState<ForgeRecipe[]>([]);
  const [queue, setQueue]           = useState<ForgeQueueEntry[]>([]);
  const [inventory, setInventory]   = useState<InventoryEntry[]>([]);
  const [walletBalanceLocal, setWalletBalanceLocal] = useState(0);
  const storeBalance = useWalletStore(s => s.balance);
  const setBalanceStore = useWalletStore(s => s.setBalance);
  const bumpInventory = useWalletStore(s => s.bumpInventory);
  const walletBalance = storeBalance ?? walletBalanceLocal;
  const [charLevel, setCharLevel]   = useState(1);
  const [charEnergy, setCharEnergy] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [actionMsg, setActionMsg]   = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // View
  const [activeTab, setActiveTab]   = useState<'recipes' | 'queue'>('recipes');
  const [selectedRecipe, setSelectedRecipe] = useState<ForgeRecipe | null>(null);
  const [forging, setForging]       = useState(false);

  // Timer tick ref
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, setTick] = useState(0); // Force re-render for timer

  // ── Load data ────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [recipesData, queueData, invData] = await Promise.all([
        fetchForgeRecipes(token),
        fetchForgeQueue(token),
        fetch('/api/user/inventory', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);

      setRecipes(recipesData);
      setQueue(queueData.queue);
      setInventory(invData.inventory?.map((e: any) => ({
        item_id: e.item?.id,
        quantity: e.quantity,
      })) ?? []);
      setWalletBalanceLocal(invData.walletBalance ?? 0);
      if (typeof invData.walletBalance === 'number') setBalanceStore(invData.walletBalance);

      // Nível do personagem
      const charRes = await fetch('/api/auth/character', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({}));
      setCharLevel(charRes?.character?.level ?? 1);
      setCharEnergy(charRes?.character?.currentEnergy ?? 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, setBalanceStore]);

  // ── Timer for countdown ──────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isOpen]);

  // ── Preload queue on mount ───────────────────────────────────────────────

  useEffect(() => {
    fetchForgeQueue(token).then(data => setQueue(data.queue)).catch(() => {});
  }, [token]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOpen = (e?: React.MouseEvent) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsOpen(true);
    setIsAnimOpen(true);
    setSelectedRecipe(null);
    setActionMsg(null);
    loadData();
  };

  const handleClose = () => {
    setIsAnimOpen(false);
    setIsHovered(false);
    closeTimer.current = setTimeout(() => {
      setIsOpen(false);
      setSelectedRecipe(null);
    }, 220);
  };

  useEffect(() => {
    const listener = () => handleOpen();
    window.addEventListener('open-forge', listener);
    return () => window.removeEventListener('open-forge', listener);
  }, [handleOpen]);

  const handleStartForge = async (recipe: ForgeRecipe) => {
    try {
      setForging(true);
      setActionMsg(null);
      const result = await startForge(token, recipe.id);
      setActionMsg({ text: result.message, type: 'success' });
      // Reload data + avisa o resto da UI (saldo/mochila mudaram)
      await loadData();
      bumpInventory();
      if (result.status === 'queued') {
        setActiveTab('queue');
        setSelectedRecipe(null);
      } else {
        setSelectedRecipe(null);
      }
    } catch (err: any) {
      setActionMsg({ text: err.message, type: 'error' });
    } finally {
      setForging(false);
    }
  };

  const handleCollect = async (queueId: string) => {
    try {
      setActionMsg(null);
      const result = await collectForgeItem(token, queueId);
      setActionMsg({ text: result.message, type: 'success' });
      await loadData();
      bumpInventory();
    } catch (err: any) {
      setActionMsg({ text: err.message, type: 'error' });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getItemName = (item: any): string => {
    if (!item) return '?';
    const baseId = item.icon_url
      ? item.icon_url.split('/').pop()?.replace(/\?.*$/, '').replace('.png', '')
      : item.id ?? item.name?.toLowerCase().replace(/\s+/g, '_');
    const key = `item.${baseId}.name` as any;
    const translated = t(key);
    return translated !== key ? translated : item.name ?? '?';
  };

  const getPlayerQty = (itemId: string): number => {
    return inventory.find(e => e.item_id === itemId)?.quantity ?? 0;
  };

  const canForge = (recipe: ForgeRecipe): boolean => {
    // Check materials
    for (const ing of recipe.forge_recipe_details ?? []) {
      if (getPlayerQty(ing.item.id) < ing.quantity) return false;
    }
    // Check walletBalance
    if ((recipe.forge_cost ?? 0) > 0 && walletBalance < (recipe.forge_cost ?? 0)) return false;
    // Check level
    if ((recipe.forge_min_xp ?? 0) > 0 && charLevel < (recipe.forge_min_xp ?? 0)) return false;
    // Check energy
    if ((recipe.forge_energy_cost ?? 0) > 0 && charEnergy < (recipe.forge_energy_cost ?? 0)) return false;
    return true;
  };

  const readyCount = queue.filter(q => q.status === 'ready' || (q.status === 'in_progress' && q.finishes_at && new Date(q.finishes_at) <= new Date())).length;
  const inProgressCount = queue.filter(q => q.status === 'in_progress' && q.finishes_at && new Date(q.finishes_at) > new Date()).length;

  return (
    <>
      {/* ── Forge button ─────────────────────────────────────────────────── */}
      {!hideButton && (
        <button
          className={styles.forgeButton}
          onClick={handleOpen}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title={t('forge.title' as any)}
          aria-label={t('forge.title' as any)}
        >
        <div className={`${styles.forgeAuraRing} ${isHovered ? styles.forgeAuraActive : ''}`} />
        <div className={styles.forgeImgWrap}>
          <img src={forgeIcon} alt="" className={styles.forgeImg} />
          {isHovered && !isOpen && (
            <>
              <span className={`${styles.forgeSpark} ${styles.forgeSpark1}`} />
              <span className={`${styles.forgeSpark} ${styles.forgeSpark2}`} />
              <span className={`${styles.forgeSpark} ${styles.forgeSpark3}`} />
            </>
          )}
        </div>
        {(readyCount > 0 || inProgressCount > 0) && (
          <span className={styles.forgeBadge}>{readyCount > 0 ? readyCount : '⏳'}</span>
        )}
        <span className={styles.tileLabel}>{t('forge.buttonLabel' as any) || 'Forja'}</span>
      </button>
      )}

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      {isOpen && createPortal(
        <div
          className={`${styles.overlay} ${isAnimOpen ? styles.overlayIn : styles.overlayOut}`}
          onClick={handleClose}
        >
          <div
            className={`${styles.modal} ${isAnimOpen ? styles.modalIn : styles.modalOut}`}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('forge.title' as any)}
          >
            {/* Header */}
            <header className={styles.modalHeader}>
              <div className={styles.headerLeft}>
                <img src={forgeIcon} alt="" className={styles.headerIcon} />
                <h2 className={styles.modalTitle}>{t('forge.title' as any)}</h2>
              </div>
              <div className={styles.headerRight}>
                <div className={styles.infoIconWrap}>
                  <div className={styles.infoIcon}>i</div>
                  <div className={styles.infoTooltip}>
                    <div className={styles.tooltipTitle}>🛠️ {t('forge.info.title' as any)}</div>
                    <p className={styles.tooltipStep}>{t('forge.info.step1' as any)}</p>
                    <p className={styles.tooltipStep}>{t('forge.info.step2' as any)}</p>
                    <p className={styles.tooltipStep}>{t('forge.info.step3' as any)}</p>
                    <p className={styles.tooltipStep}>{t('forge.info.step4' as any)}</p>
                  </div>
                </div>
                <button className={styles.closeBtn} onClick={handleClose} aria-label="Fechar">✕</button>
              </div>
            </header>

            {/* Tabs */}
            <div className={styles.tabBar}>
              <button
                className={`${styles.tab} ${activeTab === 'recipes' ? styles.tabActive : ''}`}
                onClick={() => { setActiveTab('recipes'); setSelectedRecipe(null); }}
              >
                🔨 {t('forge.tabRecipes' as any)}
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'queue' ? styles.tabActive : ''}`}
                onClick={() => { setActiveTab('queue'); setSelectedRecipe(null); }}
              >
                ⏳ {t('forge.tabQueue' as any)}
                {queue.length > 0 && ` (${queue.length})`}
              </button>
            </div>

            {/* Action Message */}
            {actionMsg && (
              <div style={{
                padding: '8px 20px',
                fontSize: 12,
                fontWeight: 600,
                background: actionMsg.type === 'success' ? 'rgba(68,204,136,0.1)' : 'rgba(255,80,80,0.1)',
                color: actionMsg.type === 'success' ? '#44cc88' : '#ff6666',
                borderBottom: '1px solid ' + (actionMsg.type === 'success' ? 'rgba(68,204,136,0.2)' : 'rgba(255,80,80,0.2)'),
              }}>
                {actionMsg.text}
              </div>
            )}

            {/* Body */}
            <div className={styles.modalBody}>
              {loading && (
                <div className={styles.centerState}>
                  <div className={styles.spinner} />
                  <span>{t('common.loading')}</span>
                </div>
              )}

              {error && (
                <div className={styles.centerState}>
                  <span style={{ color: '#ff6666' }}>{error}</span>
                </div>
              )}

              {!loading && !error && activeTab === 'recipes' && !selectedRecipe && (
                recipes.length === 0 ? (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyGlyph}>🔨</span>
                    <p className={styles.emptyText}>{t('forge.noRecipes' as any)}</p>
                  </div>
                ) : (
                  <div className={styles.recipeGrid}>
                    {recipes.map(recipe => (
                      <button
                        key={recipe.id}
                        className={styles.recipeCard}
                        onClick={() => setSelectedRecipe(recipe)}
                      >
                        <div className={styles.recipeIconRow}>
                          {recipe.icon_url ? (
                            <img src={`${recipe.icon_url}?v=4`} alt="" className={styles.recipeIcon} />
                          ) : (
                            <div className={styles.recipeIconPlaceholder}>{(recipe.name || '?').charAt(0)}</div>
                          )}
                          <span className={styles.recipeName}>{getItemName(recipe)}</span>
                        </div>
                        <div className={styles.recipeMeta}>
                          {(recipe.forge_cost ?? 0) > 0 && (
                            <span className={`${styles.recipeBadge} ${styles.badgeKamas}`}>
                              💰 {recipe.forge_cost} TF
                            </span>
                          )}
                          {(recipe.forge_time ?? 0) > 0 ? (
                            <span className={`${styles.recipeBadge} ${styles.badgeTime}`}>
                              ⏱ {formatTime(recipe.forge_time!)}
                            </span>
                          ) : (
                            <span className={`${styles.recipeBadge} ${styles.badgeTime}`}>
                              ⚡ {t('forge.instant' as any)}
                            </span>
                          )}
                          {(recipe.forge_min_xp ?? 0) > 0 && (
                            <span className={`${styles.recipeBadge} ${styles.badgeLevel}`}>
                              ⭐ Lv.{recipe.forge_min_xp}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )
              )}

              {/* ── Recipe Detail ─────────────────────────────────────────── */}
              {!loading && !error && activeTab === 'recipes' && selectedRecipe && (
                <div className={styles.forgeDetail}>
                  <button className={styles.backBtn} onClick={() => setSelectedRecipe(null)}>
                    ← {t('forge.backToRecipes' as any)}
                  </button>

                  {/* Result item */}
                  <div className={styles.forgeResultRow}>
                    {selectedRecipe.icon_url ? (
                      <img src={`${selectedRecipe.icon_url}?v=4`} alt="" className={styles.forgeResultIcon} />
                    ) : (
                      <div className={styles.recipeIconPlaceholder} style={{ width: 56, height: 56 }}>
                        {(selectedRecipe.name || '?').charAt(0)}
                      </div>
                    )}
                    <div className={styles.forgeResultInfo}>
                      <h3 className={styles.forgeResultName}>{getItemName(selectedRecipe)}</h3>
                      {selectedRecipe.description && (
                        <p className={styles.forgeResultDesc}>{selectedRecipe.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Ingredients */}
                  <div className={styles.ingredientSection}>
                    <span className={styles.sectionLabel}>
                      📦 {t('forge.ingredients' as any)}
                    </span>
                    <div className={styles.ingredientSlots}>
                      {(selectedRecipe.forge_recipe_details ?? []).map((ing, idx) => {
                        const playerQty = getPlayerQty(ing.item.id);
                        const satisfied = playerQty >= ing.quantity;
                        return (
                          <div
                            key={idx}
                            className={`${styles.ingredientSlot} ${satisfied ? styles.slotSatisfied : styles.slotMissing}`}
                          >
                            {ing.item.icon_url ? (
                              <img src={`${ing.item.icon_url}?v=4`} alt="" className={styles.ingIcon} />
                            ) : (
                              <div className={styles.ingIconPlaceholder}>
                                {(ing.item.name || '?').charAt(0)}
                              </div>
                            )}
                            <div className={styles.ingInfo}>
                              <span className={styles.ingName}>{getItemName(ing.item)}</span>
                              <span className={`${styles.ingQty} ${satisfied ? styles.qtyOk : styles.qtyMissing}`}>
                                {playerQty} / {ing.quantity}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Requirements */}
                  <div className={styles.ingredientSection}>
                    <span className={styles.sectionLabel}>
                      📋 {t('forge.requirements' as any)}
                    </span>
                    <div className={styles.requirementRow}>
                      {(selectedRecipe.forge_cost ?? 0) > 0 && (
                        <div className={styles.reqItem}>
                          <span className={styles.reqIcon}>💰</span>
                          <span className={styles.reqLabel}>{t('economy.currency' as any)}:</span>
                          <span className={`${styles.reqValue} ${walletBalance >= (selectedRecipe.forge_cost ?? 0) ? styles.reqOk : styles.reqFail}`}>
                            {walletBalance.toLocaleString()} / {selectedRecipe.forge_cost?.toLocaleString()} TF
                          </span>
                        </div>
                      )}
                      {(selectedRecipe.forge_min_xp ?? 0) > 0 && (
                        <div className={styles.reqItem}>
                          <span className={styles.reqIcon}>⭐</span>
                          <span className={styles.reqLabel}>{t('forge.minLevel' as any)}:</span>
                          <span className={`${styles.reqValue} ${charLevel >= (selectedRecipe.forge_min_xp ?? 0) ? styles.reqOk : styles.reqFail}`}>
                            {charLevel} / {selectedRecipe.forge_min_xp}
                          </span>
                        </div>
                      )}
                      {(selectedRecipe.forge_energy_cost ?? 0) > 0 && (
                        <div className={styles.reqItem}>
                          <span className={styles.reqIcon}>⚡</span>
                          <span className={styles.reqLabel}>{t('energy.label' as any)}:</span>
                          <span className={`${styles.reqValue} ${charEnergy >= (selectedRecipe.forge_energy_cost ?? 0) ? styles.reqOk : styles.reqFail}`}>
                            {charEnergy} / {selectedRecipe.forge_energy_cost}
                          </span>
                        </div>
                      )}
                      <div className={styles.reqItem}>
                        <span className={styles.reqIcon}>⏱</span>
                        <span className={styles.reqLabel}>{t('forge.time' as any)}:</span>
                        <span className={styles.reqValue}>
                          {(selectedRecipe.forge_time ?? 0) > 0
                            ? formatTime(selectedRecipe.forge_time!)
                            : t('forge.instant' as any)
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Forge button */}
                  <div className={styles.forgeActionRow}>
                    <button
                      className={styles.forgeBtn}
                      disabled={!canForge(selectedRecipe) || forging}
                      onClick={() => handleStartForge(selectedRecipe)}
                    >
                      {forging ? `⏳ ${t('forge.forging' as any)}` : `🔨 ${t('forge.forgeBtn' as any)}`}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Queue Tab ─────────────────────────────────────────────── */}
              {!loading && !error && activeTab === 'queue' && (
                queue.length === 0 ? (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyGlyph}>⏳</span>
                    <p className={styles.emptyText}>{t('forge.noQueue' as any)}</p>
                  </div>
                ) : (
                  <div className={styles.queueList}>
                    {queue.map(entry => {
                      const isReady = entry.status === 'ready' || 
                        (entry.status === 'in_progress' && entry.finishes_at && new Date(entry.finishes_at) <= new Date());
                      const remaining = entry.finishes_at 
                        ? Math.max(0, Math.floor((new Date(entry.finishes_at).getTime() - Date.now()) / 1000))
                        : 0;

                      return (
                        <div key={entry.id} className={`${styles.queueCard} ${isReady ? styles.queueReady : ''}`}>
                          {entry.item?.icon_url ? (
                            <img src={`${entry.item.icon_url}?v=4`} alt="" className={styles.queueIcon} />
                          ) : (
                            <div className={styles.recipeIconPlaceholder} style={{ width: 44, height: 44 }}>
                              {(entry.item?.name || '?').charAt(0)}
                            </div>
                          )}
                          <div className={styles.queueInfo}>
                            <span className={styles.queueName}>
                              {entry.item ? getItemName(entry.item) : '...'}
                            </span>
                            <span className={`${styles.queueStatus} ${isReady ? styles.statusReady : styles.statusInProgress}`}>
                              {isReady
                                ? `✅ ${t('forge.statusReady' as any)}`
                                : `⏳ ${t('forge.statusInProgress' as any)}`
                              }
                            </span>
                          </div>
                          {isReady ? (
                            <button
                              className={styles.collectBtn}
                              onClick={() => handleCollect(entry.id)}
                            >
                              📦 {t('forge.collect' as any)}
                            </button>
                          ) : (
                            <span className={styles.queueTimer}>
                              {formatTime(remaining)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
